import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import styled from "styled-components";
import { rgba, p3, media } from "../styles/stylesTheme";
import { getCSSImageUrl } from "../utils/mediaUrl";
import { getHomeLanguageCode } from "../utils/localeUtils";
import {
    apiGetBjornDecks,
    apiCheckBjornCard,
    apiAddBjornWord,
    apiCreateBjornDeck,
} from "../api/apiBjornIntegration";

const ModalAddWordToFolder = ({
    isOpen,
    onClose,
    tooltipData,
    setTooltipData,
    bjornUserId,
    triggerNotification,
}) => {
    const [decks, setDecks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newDeckName, setNewDeckName] = useState(false);
    const [successDeckId, setSuccessDeckId] = useState(null);
    const backdropRef = useRef(null);

    // Fetch decks when modal opens (filtered by language)
    useEffect(() => {
        if (!isOpen || !bjornUserId || !tooltipData?.sourceLanguage) {
            setLoading(true);
            setDecks([]);
            return undefined;
        }

        let cancelled = false;
        setLoading(true);

        const fetchDecks = async () => {
            try {
                const homeLanguage = getHomeLanguageCode();
                const result = await apiGetBjornDecks(
                    homeLanguage,
                    tooltipData.sourceLanguage
                );
                if (!cancelled) {
                    setDecks(result.decks || []);
                }
            } catch (error) {
                console.error("Error fetching decks:", error);
                if (!cancelled) {
                    setDecks([]);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchDecks();
        return () => {
            cancelled = true;
        };
    }, [isOpen, bjornUserId, tooltipData?.sourceLanguage]);

    // Handle escape key
    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key === "Escape" && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener("keydown", handleEscape);
        }

        return () => {
            document.removeEventListener("keydown", handleEscape);
        };
    }, [isOpen, onClose]);

    // Handle backdrop click
    const handleBackdropClick = useCallback(
        (event) => {
            if (event.target === backdropRef.current) {
                onClose();
            }
        },
        [onClose]
    );

    const handleAddWordToDeck = useCallback(
        async (deck) => {
            if (!tooltipData?.word) return;

            // Show success flash immediately
            setSuccessDeckId(deck.id);
            setTimeout(() => {
                setSuccessDeckId(null);
            }, 1000);

            try {
                // Check if word already exists
                const checkResult = await apiCheckBjornCard(
                    deck.id,
                    tooltipData.word
                );
                if (checkResult.exists) {
                    triggerNotification?.(
                        "Word already exists in this deck",
                        "warn"
                    );
                    return;
                }

                // Add the word
                await apiAddBjornWord(
                    deck.id,
                    tooltipData.word,
                    tooltipData.definition || ""
                );

                triggerNotification?.("Word added", "success");
            } catch (error) {
                console.error("Error adding word:", error);
                if (error.response?.status === 409) {
                    triggerNotification?.(
                        "Word already exists in this deck",
                        "warn"
                    );
                } else {
                    triggerNotification?.("Failed to add word", "error");
                }
            }
        },
        [tooltipData, triggerNotification]
    );

    const handleAddNewDeck = async (name) => {
        if (!name.trim()) return;

        try {
            const targetLanguage = tooltipData?.sourceLanguage;
            if (!targetLanguage) return;
            const homeLanguage = getHomeLanguageCode(); // 'US' or 'EN' based on locale
            const result = await apiCreateBjornDeck(
                name.trim(),
                homeLanguage,
                targetLanguage
            );
            if (result.success) {
                const newDeck = {
                    id: result.id,
                    name: result.name,
                    slug: result.slug,
                };
                setDecks((prev) => [...prev, newDeck]);
                setNewDeckName(false);

                // Auto-add the word to the new deck
                handleAddWordToDeck(newDeck);
            }
        } catch (error) {
            console.error("Error creating deck:", error);
            if (error.response?.status === 409) {
                triggerNotification?.(
                    "A folder with this name already exists",
                    "warn"
                );
            } else {
                triggerNotification?.("Failed to create folder", "error");
            }
        }
    };

    const handleWordChange = (e) => {
        setTooltipData((prev) => ({
            ...prev,
            word: e.target.value,
        }));
    };

    const handleDefinitionChange = (e) => {
        setTooltipData((prev) => ({
            ...prev,
            definition: e.target.value,
        }));
    };

    return (
        <AnimatePresence mode="wait">
            {isOpen && (
                <ModalBackdrop
                    ref={backdropRef}
                    onClick={handleBackdropClick}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{
                        opacity: 0,
                        transition: { duration: 0.2, ease: "easeInOut" },
                    }}
                    transition={{ duration: 0.2, ease: "easeInOut" }}
                >
                    {!loading && <StyledModal
                        onClick={(e) => e.stopPropagation()}
                        initial={{
                            opacity: 0,
                            y: "-45%",
                            x: "-50%",
                        }}
                        animate={{ opacity: 1, y: "-50%", x: "-50%" }}
                        exit={{
                            opacity: 0,
                            y: "-45%",
                            x: "-50%",
                            transition: {
                                y: {
                                    type: "tween",
                                    duration: 0.3,
                                    ease: "easeIn",
                                },
                                opacity: {
                                    duration: 0.2,
                                },
                            },
                        }}
                        transition={{
                            y: {
                                type: "spring",
                                bounce: 1,
                                stiffness: 300,
                                damping: 16,
                                duration: 0.3,
                            },
                            opacity: {
                                duration: 0.2,
                                ease: "easeInOut",
                            },
                        }}
                    >
                        <DecksContainer>
                            <HeaderContainer>
                                <h2>Add to topic</h2>
                                <CloseButton onClick={onClose} />
                            </HeaderContainer>

                            <WordDefContainer>
                                <InputWrapper>
                                    <label>Word / Phrase</label>
                                    <input
                                        id="word"
                                        type="text"
                                        value={tooltipData?.word || ""}
                                        onChange={handleWordChange}
                                        maxLength={255}
                                    />
                                </InputWrapper>
                                <InputWrapper>
                                    <label>Definition</label>
                                    <input
                                        id="definition"
                                        type="text"
                                        value={tooltipData?.definition || ""}
                                        onChange={handleDefinitionChange}
                                        maxLength={255}
                                    />
                                </InputWrapper>
                            </WordDefContainer>

                            <DeckListContainer>
                                {loading ? (
                                    <LoadingMessage>
                                        Loading folders...
                                    </LoadingMessage>
                                ) : decks.length === 0 ? (
                                    <EmptyMessage>
                                        No folders yet. Create one below!
                                    </EmptyMessage>
                                ) : (
                                    decks.map((deck) => (
                                        <AddWordToDeckButton
                                            key={deck.id}
                                            onClick={() =>
                                                handleAddWordToDeck(deck)
                                            }
                                            data-deck-id={deck.id}
                                            isSuccess={
                                                successDeckId === deck.id
                                            }
                                        >
                                            {deck.name}
                                        </AddWordToDeckButton>
                                    ))
                                )}
                                <AddNewDeckButton
                                    onClick={() => {
                                        setNewDeckName(true);
                                        setTimeout(() => {
                                            document
                                                .getElementById(
                                                    "newDeckNameInput"
                                                )
                                                ?.focus();
                                        }, 0);
                                    }}
                                >
                                    Add Folder
                                </AddNewDeckButton>
                            </DeckListContainer>

                            <AnimatePresence>
                                {newDeckName && (
                                    <NewDeckNameInput
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{
                                            opacity: 0,
                                            y: 20,
                                            transition: {
                                                y: {
                                                    type: "tween",
                                                    duration: 0.3,
                                                    ease: "easeIn",
                                                },
                                                opacity: {
                                                    duration: 0.2,
                                                },
                                            },
                                        }}
                                        transition={{
                                            y: {
                                                type: "spring",
                                                bounce: 1,
                                                stiffness: 300,
                                                damping: 16,
                                                duration: 0.3,
                                            },
                                            opacity: {
                                                duration: 0.2,
                                                ease: "easeInOut",
                                            },
                                        }}
                                    >
                                        <div>
                                            <input
                                                id="newDeckNameInput"
                                                type="text"
                                                placeholder="Folder name"
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        handleAddNewDeck(
                                                            e.target.value
                                                        );
                                                    }
                                                }}
                                                maxLength={255}
                                            />
                                            <button
                                                onClick={() =>
                                                    setNewDeckName(false)
                                                }
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={() => {
                                                    const input =
                                                        document.getElementById(
                                                            "newDeckNameInput"
                                                        );
                                                    if (input) {
                                                        handleAddNewDeck(
                                                            input.value
                                                        );
                                                    }
                                                }}
                                            >
                                                Add Folder
                                            </button>
                                        </div>
                                    </NewDeckNameInput>
                                )}
                            </AnimatePresence>
                        </DecksContainer>
                    </StyledModal>}
                </ModalBackdrop>
            )}
        </AnimatePresence>
    );
};

// Styled Components - matching main site exactly

const ModalBackdrop = styled(motion.div)`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 110000;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: auto;
    backdrop-filter: blur(0.2em);
    -webkit-backdrop-filter: blur(0.2em);
`;

const StyledModal = styled(motion.div)`
    position: fixed;
    top: 50%;
    left: 50%;
    z-index: 110001;
    font-size: 0.9em;
    width: 30em;
    max-height: 90vh;
    max-width: 95vw;
    border-radius: 1.25em;
    overflow: hidden;
    color: ${(props) => rgba(props.theme, "black", 1)};
    background: ${(props) => rgba(props.theme, "white", 1)};
    box-shadow: 0 20px 60px -12px ${({ theme }) => rgba(theme, "black", 0.25)},
        0 8px 24px -8px ${({ theme }) => rgba(theme, "black", 0.1)};
    pointer-events: auto;
    overflow-y: auto;

    ${media.mobile} {
        width: 92%;
        max-width: 92%;
        font-size: 1em;
        border-radius: 1em;
    }
`;

const DecksContainer = styled.div`
    width: 100%;
    background: ${(props) => rgba(props.theme, "white", 1)};

    &::-webkit-scrollbar {
        display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;

    ${media.mobile} {
        height: calc(100svh - 34em);
    }
`;

const HeaderContainer = styled.div`
    background: ${(props) => rgba(props.theme, "colorAccent", 0.8)};
    background: ${(props) => p3(props.theme, "pcolorAccent", 0.8)};
    padding: 0.1em 1.4em 0.05em;
    text-align: center;
    border-top-left-radius: 1.25em;
    border-top-right-radius: 1.25em;
    position: relative;

    h2 {
        font-size: 1.3em;
        font-family: var(--bold);
        color: ${(props) => rgba(props.theme, "white", 1)};
        position: relative;
    }
`;

const CloseButton = styled.button`
    position: absolute;
    top: 50%;
    right: 1.2em;
    transform: translateY(-50%);
    width: 2em;
    height: 2em;
    border: none;
    background: none;
    cursor: pointer;
    padding: 0;
    display: none;
    opacity: 0.8;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 1;
    }

    &:active {
        transform: translateY(-50%) scale(0.95);
    }

    &::before {
        content: "";
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 1.2em;
        height: 1.2em;
        background: ${(props) => rgba(props.theme, "white", 1)};
        mask-image: url("${getCSSImageUrl("img/icon-x.svg")}");
        -webkit-mask-image: url("${getCSSImageUrl("img/icon-x.svg")}");
        mask-position: center;
        -webkit-mask-position: center;
        mask-repeat: no-repeat;
        -webkit-mask-repeat: no-repeat;
        mask-size: contain;
        -webkit-mask-size: contain;
    }

    ${media.tablet} {
        display: block;
    }

    ${media.mobile} {
        right: 1em;
    }
`;

const WordDefContainer = styled.div`
    padding: 1em 1.4em 1.2em;
    background: ${(props) => rgba(props.theme, "colorUI", 0.5)};
    background: ${(props) => p3(props.theme, "pcolorUI", 0.5)};
    z-index: 10;
    text-align: center;

    label {
        display: block;
        font-size: 0.8em;
        opacity: 0.5;
        color: ${(props) => rgba(props.theme, "black", 1)};
        color: ${(props) => p3(props.theme, "black", 1)};
        mix-blend-mode: unset;
        -webkit-mix-blend-mode: unset;
        padding-bottom: 0.6em;
    }

    input {
        margin: 0 0 0.8em;
        width: calc(100% - 2.4em);
        text-align: center;
        opacity: 1;
        transition: 150ms;
        border: none;
        border-radius: 0.5em;
        padding: 0.9em 1.2em;
        font-size: 1.1em;
        background: ${(props) => rgba(props.theme, "white", 1)};
        color: ${({ theme }) => rgba(theme, "black", 1)};
        color: ${({ theme }) => p3(theme, "black", 1)};

        &:last-child {
            margin-bottom: 0.4em;
        }

        &::placeholder {
            color: ${({ theme }) => rgba(theme, "black", 0.2)};
        }

        &:hover {
            background: ${(props) => rgba(props.theme, "white", 0.9)};
        }

        &:focus {
            background: ${(props) => rgba(props.theme, "white", 1)};
            outline: none;
        }
    }
`;

const InputWrapper = styled.div`
    position: relative;

    .translation-loader {
        position: absolute;
        right: 1em;
        top: 2.2em;
        width: 1em;
        height: 1em;
        opacity: 0.8;
        z-index: 10;
    }
`;

const DeckListContainer = styled.div`
    float: left;
    width: 20em;
    height: auto;
    max-height: 30em;
    overflow-y: scroll;
    position: relative;
    padding-top: 0.4em;
    width: 100%;
    padding-bottom: 1em;
    overflow-y: scroll;
    background: ${(props) => rgba(props.theme, "white", 1)};

    .deck-list {
        width: 100%;
        height: 100%;
        overflow-y: scroll;
        position: relative;
        font-size: 105%;
    }

    ${media.mobile} {
        max-height: 100%;
    }
`;

const LoadingMessage = styled.div`
    text-align: center;
    padding: 2em;
    color: ${(props) => rgba(props.theme, "black", 0.5)};
`;

const EmptyMessage = styled.div`
    text-align: center;
    padding: 2em;
    color: ${(props) => rgba(props.theme, "black", 0.5)};
`;

const AddWordToDeckButton = styled.button.withConfig({
    shouldForwardProp: (prop) => prop !== "isSuccess",
})`
    margin: 0.6em 0.8em 0;
    padding: 1em;
    color: ${(props) =>
        props.isSuccess
            ? rgba(props.theme, "colortextCorrect", 1)
            : rgba(props.theme, "black", 1)};
    border: none;
    cursor: pointer;
    font-size: 1.2em;
    border-radius: 0.8em;
    opacity: 0.9;
    width: calc(100% - 1.6em);
    transition: 150ms;
    position: relative;
    background: ${(props) =>
        props.isSuccess
            ? rgba(props.theme, "colorCorrect", 1)
            : rgba(props.theme, "colorCorrect", 0.1)};
    text-align: left;

    &:after {
        content: "";
        position: absolute;
        top: calc(50% + 1px);
        right: 0.85em;
        transform: translateY(-50%);
        width: 1.2em;
        height: 1.2em;
        background: ${(props) =>
            props.isSuccess
                ? rgba(props.theme, "colortextCorrect", 1)
                : rgba(props.theme, "black", 1)};
        mask: url("${getCSSImageUrl("img/icon-add.svg")}") no-repeat center /
            contain;
        -webkit-mask: url("${getCSSImageUrl("img/icon-add.svg")}") no-repeat
            center / contain;
        mask-size: 100%;
        -webkit-mask-size: 100%;
    }

    &:hover {
        background: ${(props) =>
            props.isSuccess
                ? rgba(props.theme, "colorCorrect", 1)
                : rgba(props.theme, "colorCorrect", 0.12)};
    }

    &:active {
        transform: scale(0.99);
    }
`;

const AddNewDeckButton = styled.button`
    margin: 0.6em 0.8em 0;
    padding: 1em;
    border: none;
    cursor: pointer;
    font-size: 1.2em;
    border-radius: 0.8em;
    opacity: 0.9;
    width: calc(100% - 1.6em);
    transition: 150ms;
    position: relative;
    border-radius: 0.75rem;
    border: 1px solid ${(props) => rgba(props.theme, "colorUI", 1)};
    border: 1px solid ${(props) => p3(props.theme, "pcolorUI", 1)};
    background: ${(props) => rgba(props.theme, "colorUI", 0.5)};
    background: ${(props) => p3(props.theme, "pcolorUI", 0.5)};
    color: ${(props) => rgba(props.theme, "black", 1)};
    color: ${(props) => p3(props.theme, "black", 1)};

    &:hover {
        background: ${(props) => rgba(props.theme, "colorUI", 0.7)};
        background: ${(props) => p3(props.theme, "pcolorUI", 0.7)};
    }

    &:active {
        transform: scale(0.985);
    }
`;

const NewDeckNameInput = styled(motion.div)`
    position: absolute;
    top: -5%;
    left: -5%;
    width: 110%;
    height: 110%;
    background: ${(props) => rgba(props.theme, "colorUI", 0.7)};
    background: ${(props) => p3(props.theme, "pcolorUI", 0.7)};
    backdrop-filter: blur(0.3em);
    -webkit-backdrop-filter: blur(0.3em);

    > div {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: calc(80% - 4em);
        background: ${(props) => rgba(props.theme, "gradL", 0.7)};
        background: ${(props) => p3(props.theme, "pgradL", 0.7)};
        text-align: center;
        border-radius: 0.8rem;
        padding: 2em;

        label {
            font-size: 0.8em;
            opacity: 0.5;
            color: #6e2c1a;
            color: color(display-p3 0.4 0.1882 0.1216);
            mix-blend-mode: unset;
            -webkit-mix-blend-mode: unset;
            margin-bottom: 0.1em;
        }

        input {
            margin: 0 0 0.6em;
            width: calc(100% - 1.95em);
            text-align: center;
            background: #fff;
            color: #6e2c1a;
            color: color(display-p3 0.4 0.1882 0.1216);
            opacity: 1;
            margin-bottom: 0.4em;
            background: ${(props) => rgba(props.theme, "white", 1)};
            color: ${({ theme }) => rgba(theme, "black", 1)};
            color: ${({ theme }) => p3(theme, "black", 1)};
            transition: 150ms;
            border: none;
            border-radius: 0.4em;
            padding: 0.7em 1em;
            font-size: 1em;

            &::placeholder {
                color: ${({ theme }) => rgba(theme, "black", 0.3)};
                color: ${({ theme }) => p3(theme, "black", 0.3)};
            }

            &:hover {
                background: ${(props) => rgba(props.theme, "white", 0.9)};
            }

            &:focus {
                background: ${(props) => rgba(props.theme, "white", 1)};
                outline: none;
            }
        }

        button {
            margin: 1em 0.5em 0;
            border: none;
            padding: 0.6em 0.6em 0.55em;
            border-radius: 0.5em;
            font-size: 1.2em;
            border: 1px solid ${(props) => rgba(props.theme, "colorUI", 1)};
            border: 1px solid ${(props) => p3(props.theme, "pcolorUI", 1)};
            background: ${(props) => rgba(props.theme, "colorUI", 0.5)};
            background: ${(props) => p3(props.theme, "pcolorUI", 0.5)};
            color: ${(props) => rgba(props.theme, "black", 1)};
            color: ${(props) => p3(props.theme, "black", 1)};
            cursor: pointer;

            &:hover {
                background: ${(props) => rgba(props.theme, "colorUI", 0.7)};
                background: ${(props) => p3(props.theme, "pcolorUI", 0.7)};
            }

            &:active {
                transform: scale(0.985);
            }
        }
    }
`;

export default ModalAddWordToFolder;
